import { useEffect, useRef } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import type { UnifiedWallet } from '../auth/useWallet';
import type { Subaccount } from '../hooks/useSubaccounts';
import { useWithdraw, type WithdrawStep } from '../hooks/useWithdraw';
import { formatOre, formatSol, shortenPubkey } from '../lib/evore';

export function SubaccountCard({
  wallet,
  subaccount,
  onAfterAction,
}: {
  wallet: UnifiedWallet;
  subaccount: Subaccount;
  onAfterAction: () => void;
}) {
  const { t } = useTranslation();
  const withdraw = useWithdraw(wallet);

  const stepLabels: Record<WithdrawStep, string> = {
    idle: '',
    preparing: t('sunset.card.stepPreparing'),
    signing: t('sunset.card.stepSigning'),
    submitting: t('sunset.card.stepSubmitting'),
    confirming: t('sunset.card.stepConfirming'),
    success: t('sunset.card.stepSuccess'),
    error: '',
  };

  const hasSol = subaccount.mmaLamports > 0n;
  const hasOre = subaccount.liveRefinedOre > 0n || subaccount.rewardsOre > 0n;
  // claimableOre folds in the treasury-factor-inferred accrual that the on-chain
  // refinedOre field doesn't reflect between checkpoints. See
  // src/sunset/lib/evore.ts inferLiveRefinedOre + computeClaimableOre.
  const oreOnClaim = Number(subaccount.claimableOre);
  const busy =
    withdraw.step !== 'idle' &&
    withdraw.step !== 'success' &&
    withdraw.step !== 'error';

  // Fire onAfterAction once per transition INTO 'success'. Without the ref
  // guard, a parent re-render that hands us a new onAfterAction reference
  // would trigger the effect again while step is still 'success' → infinite
  // refresh loop. Ref ignores onAfterAction identity changes.
  const onAfterRef = useRef(onAfterAction);
  onAfterRef.current = onAfterAction;
  const lastStep = useRef<WithdrawStep>('idle');
  useEffect(() => {
    if (withdraw.step === 'success' && lastStep.current !== 'success') {
      onAfterRef.current();
    }
    lastStep.current = withdraw.step;
  }, [withdraw.step]);

  const depositAddress = subaccount.managedMinerAuth.toBase58();

  return (
    <div className="win98-sunken win98-subaccount-card">
      <div className="win98-mb-md">
        <div className="win98-text-normal win98-text-bold win98-mb-xs">
          {t('sunset.card.addressLabel')}
        </div>
        <div className="win98-flex-row-stretch">
          <div
            className="win98-balance-value-large"
            style={{ flex: 1, textAlign: 'left', fontFamily: 'monospace' }}
          >
            {shortenPubkey(depositAddress, 6)}
          </div>
          <button
            className="win98-button win98-button-primary"
            onClick={() => navigator.clipboard.writeText(depositAddress)}
            title={t('sunset.card.copyTitle')}
            style={{ padding: '4px 8px' }}
          >
            <i className="fa-solid fa-copy" />
          </button>
        </div>
      </div>

      <div className="win98-section-heading win98-mb-xs">
        {t('sunset.card.balances')}
      </div>
      <div className="win98-sunken portfolio-sunken-group win98-mb-md">
        <div className="portfolio-stat-grid">
          <div className="portfolio-stat-item">
            <span className="portfolio-stat-label">{t('sunset.card.sol')}</span>
            <span
              className={`portfolio-stat-value ${hasSol ? 'positive' : ''}`}
            >
              {formatSol(subaccount.mmaLamports, 7)}
            </span>
          </div>
          <div className="portfolio-stat-item">
            <span className="portfolio-stat-label">
              {t('sunset.card.unrefinedOre')}
            </span>
            <span className="portfolio-stat-value win98-text-ore">
              {formatOre(subaccount.rewardsOre, 9)}
            </span>
          </div>
          <div className="portfolio-stat-item">
            <span className="portfolio-stat-label">
              {t('sunset.card.refinedOre')}
            </span>
            <span className="portfolio-stat-value win98-text-ore">
              {formatOre(subaccount.liveRefinedOre, 9)}
            </span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="win98-button"
          onClick={() => void withdraw.claimOre(subaccount.manager)}
          disabled={busy || !hasOre}
          style={{ flex: 1, padding: '6px 12px' }}
          title={
            hasOre
              ? t('sunset.card.claimOreTooltip', {
                  amount: (oreOnClaim / 10 ** 11).toFixed(6),
                })
              : t('sunset.card.noOreToClaim')
          }
        >
          {busy ? stepLabels[withdraw.step] : t('sunset.card.claimOre')}
        </button>
        <button
          className="win98-button win98-button-primary"
          onClick={() => void withdraw.withdrawAll(subaccount.manager)}
          disabled={busy || !hasSol}
          style={{ flex: 1, padding: '6px 12px' }}
          title={t('sunset.card.withdrawTooltip')}
        >
          {busy ? stepLabels[withdraw.step] : t('sunset.card.withdrawAllSol')}
        </button>
      </div>

      {withdraw.signature && (
        <div
          className="win98-text-normal"
          style={{ fontSize: 11, marginTop: 8 }}
        >
          <i className="fa-solid fa-check" /> {t('sunset.card.lastTx')}{' '}
          <a
            href={`https://solscan.io/tx/${withdraw.signature}`}
            target="_blank"
            rel="noreferrer"
          >
            {shortenPubkey(withdraw.signature, 8)}
          </a>
        </div>
      )}

      {withdraw.error && (
        <div className="win98-modal-error" style={{ marginTop: 8 }}>
          <i className="fa-solid fa-triangle-exclamation" /> {t(withdraw.error)}
          <button
            className="win98-button"
            onClick={withdraw.reset}
            style={{ marginLeft: 8, padding: '2px 8px' }}
          >
            {t('sunset.card.dismiss')}
          </button>
        </div>
      )}
    </div>
  );
}
